import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderTree,
  UtensilsCrossed,
  Package as PackageIcon,
  Mailbox,
  ScrollText,
  CalendarCheck2,
  Users,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useApp } from "@/context/AppContext";

export default function AdminLayout() {
  const { t, adminInfo, logoutAdmin } = useApp();
  const navigate = useNavigate();

  const nav = [
    { to: "/admin", label: t("admin.dashboard"), icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
    { to: "/admin/categories", label: t("admin.categories"), icon: FolderTree, testid: "admin-nav-categories" },
    { to: "/admin/dishes", label: t("admin.dishes"), icon: UtensilsCrossed, testid: "admin-nav-dishes" },
    { to: "/admin/packages", label: t("admin.packages"), icon: PackageIcon, testid: "admin-nav-packages" },
    { to: "/admin/enquiries", label: t("admin.enquiries"), icon: Mailbox, testid: "admin-nav-enquiries" },
    { to: "/admin/quotes", label: "Quotes", icon: ScrollText, testid: "admin-nav-quotes" },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck2, testid: "admin-nav-bookings" },
    { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-nav-customers" },
    { to: "/admin/emails", label: "Email log", icon: Mailbox, testid: "admin-nav-emails" },
    { to: "/admin/settings", label: t("admin.settings"), icon: SettingsIcon, testid: "admin-nav-settings" },
  ];

  const doLogout = () => {
    logoutAdmin();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-[#F6F5F1]">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border/70 bg-card p-5 lg:flex">
        <div className="mb-8">
          <BrandLogo />
        </div>
        <nav className="flex flex-col gap-1" data-testid="admin-sidenav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-secondary/80 hover:bg-muted"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto space-y-2 pt-6">
          <div className="rounded-xl border border-border/70 bg-background p-3 text-xs">
            <div className="text-muted-foreground">Signed in as</div>
            <div className="mt-0.5 truncate font-semibold text-secondary" data-testid="admin-current-user">
              {adminInfo?.email || "—"}
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-logout-button"
            className="flex w-full items-center gap-2 rounded-xl border border-border/70 bg-background px-3 py-2 text-sm font-medium text-secondary hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
          >
            <LogOut className="h-4 w-4" /> {t("admin.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-border/70 bg-card px-4 lg:hidden">
          <BrandLogo compact />
          <button
            onClick={doLogout}
            data-testid="admin-logout-button-mobile"
            className="text-xs font-semibold uppercase tracking-[0.12em] text-destructive"
          >
            {t("admin.logout")}
          </button>
        </header>

        {/* Mobile nav */}
        <div className="flex gap-1 overflow-x-auto border-b border-border/70 bg-card px-3 py-2 lg:hidden">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-secondary/80 hover:bg-muted"
                }`
              }
            >
              <n.icon className="h-3.5 w-3.5" />
              {n.label}
            </NavLink>
          ))}
        </div>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
